// client/src/components/pages/Geofencing/GeofenceForm.tsx (converted from .js, ST-00239)
// The zone editor workspace: a full-screen split view with the map as the main
// thing (left / top) and the zone's fields in a side panel (right / below).
//
// ST-00239 replaced the terra-draw drawing control with the zone editor in
// ./zoneEditor (polygon, rectangle and circle drawing; corner, midpoint, move
// and resize handles; undo / redo; snapping; live area; self-intersection and
// overlap checks; GeoJSON / KML import and GeoJSON export; search). The save
// payload is unchanged: polygons send `coordinates` ([lat, lng] pairs, server
// computes the centroid); circles send latitude / longitude / radiusMeters.
//
// LexiStar M1-C: when the server reports `versioning` (migration 021 applied)
// the panel also edits the versioned configuration — purpose type + roles,
// rules, debounce, schedule, validity (GeofenceConfigFields) — offers "test
// against history" and a version history drawer. Without it the panel edits the
// zone type and the site.
//
// ST-00269: the legacy alert settings (Alert on entry, Alert on exit, Dwell Alert (minutes))
// are gone from the form: only the retired legacy evaluator read them. A zone's alerts are
// its rules (the Rules section), which the LexiStar engine evaluates.
//
// ST-00282: with `alertSwitch` (migration 072) the panel also shows the zone's Alerts switch
// (GeofenceConfigFields' Alerts section) and the save sends `alertsEnabled`.
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ComponentType, FormEvent } from 'react';
import { FaHistory } from 'react-icons/fa';
import { Download, Eraser, FileUp, Focus, RotateCcw, X } from 'lucide-react';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import {
  ZONE_TYPES as ZONE_TYPES_JS,
  DEFAULT_ZONE_TYPE,
  getZoneType as getZoneTypeJs,
} from './zoneTypes';
import GeofenceConfigFields, { type GeofenceVersionedForm } from './GeofenceConfigFields';
import TestAgainstHistoryJs from './TestAgainstHistory';
import GeofenceVersionHistoryJs from './GeofenceVersionHistory';
import {
  buildSavePayload as buildSavePayloadJs,
  draftGeometry as draftGeometryJs,
  validateVersionedForm as validateVersionedFormJs,
  versionedFormFromGeofence as versionedFormFromGeofenceJs,
} from './geofenceConfig';
import type { GeofenceRow } from '../../../types/clientPages/geofencing';
import ZoneEditor, { type EditorSite } from './zoneEditor/ZoneEditor';
import type { ContextZone, EditorOverlay, ZoneEditorMapHandle } from './zoneEditor/ZoneEditorMap';
import ZoneImportDialog from './zoneEditor/ZoneImportDialog';
import { initialEditorState, zoneEditorReducer } from './zoneEditor/zoneEditorState';
import {
  MAX_RADIUS_M,
  MIN_RADIUS_M,
  formatArea,
  formatLength,
  isFiniteLngLat,
  shapeAreaM2,
  shapeBounds,
  shapePerimeterM,
  shapeProblems,
  shapeWarnings,
  shapesEqual,
  shapesOverlap,
  type Bounds,
  type LngLat,
  type ZoneShape,
} from './zoneEditor/zoneGeometry';
import {
  exportFileName,
  geometryFields,
  shapeFromGeofence,
  shapeToFeature,
} from './zoneEditor/zoneFormat';

/** The zone form's submit payload (the server validates it). */
export type GeofencePayload = Record<string, unknown>;

interface ZoneTypeMeta {
  id: string;
  singular: string;
  color: string;
}

interface LocationRow {
  location_id: number;
  location_name: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

/** The non-geometry fields (the geometry comes from the editor's shape). */
interface ZoneFields {
  name: string;
  description: string;
  zoneType: string;
  linkedLocationId: string | number;
}

// Typed views of the JavaScript modules this component uses.
const ZONE_TYPES = ZONE_TYPES_JS as unknown as ZoneTypeMeta[];
const getZoneType = getZoneTypeJs as unknown as (zone: GeofenceRow) => string;
const TestAgainstHistory = TestAgainstHistoryJs as unknown as ComponentType<{
  geofenceId: number | null;
  draft: Record<string, unknown> | null;
}>;
const GeofenceVersionHistory = GeofenceVersionHistoryJs as unknown as ComponentType<{
  geofence: GeofenceRow | null;
  isOpen: boolean;
  onClose: () => void;
}>;
const buildSavePayload = buildSavePayloadJs as unknown as (
  form: Record<string, unknown>,
  versioned: GeofenceVersionedForm | null,
  opts: { versioning: boolean; alertSwitch?: boolean }
) => GeofencePayload;
const draftGeometry = draftGeometryJs as unknown as (
  form: Record<string, unknown>
) => Record<string, unknown> | null;
const validateVersionedForm = validateVersionedFormJs as unknown as (
  v: GeofenceVersionedForm
) => string[];
const versionedFormFromGeofence = versionedFormFromGeofenceJs as unknown as (
  g: GeofenceRow | null
) => GeofenceVersionedForm;

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-surface text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const smallButton =
  'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-surface px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

const colorFor = (zone: GeofenceRow): string =>
  ZONE_TYPES.find((t) => t.id === getZoneType(zone))?.color ?? '#14b8a6';

function fieldsFrom(geofence: GeofenceRow | null): ZoneFields {
  return {
    name: geofence?.Name || '',
    description: geofence?.description || '',
    zoneType: geofence?.ZoneType || DEFAULT_ZONE_TYPE,
    linkedLocationId: geofence?.LinkedLocationID ?? '',
  };
}

/** Radius box: typed text is kept while focused; every valid value is one undo step per burst. */
function RadiusInput({ radiusM, onChange }: { radiusM: number; onChange: (r: number) => void }) {
  const [text, setText] = useState(String(Math.round(radiusM * 10) / 10));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(String(Math.round(radiusM * 10) / 10));
  }, [radiusM, focused]);
  const n = Number(text);
  const bad = text !== '' && (!Number.isFinite(n) || n < MIN_RADIUS_M || n > MAX_RADIUS_M);
  return (
    <div>
      <label htmlFor="ze-radius" className="text-xs font-medium text-gray-600">
        Radius (m)
      </label>
      <input
        id="ze-radius"
        type="number"
        inputMode="decimal"
        min={MIN_RADIUS_M}
        max={MAX_RADIUS_M}
        step="1"
        value={text}
        aria-invalid={bad}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value);
          const v = Number(e.target.value);
          if (
            e.target.value !== '' &&
            Number.isFinite(v) &&
            v >= MIN_RADIUS_M &&
            v <= MAX_RADIUS_M
          ) {
            onChange(v);
          }
        }}
        className={`${inputClass} mt-1 w-32`}
      />
      {bad && (
        <p className="mt-1 text-xs text-red-600">
          Enter {MIN_RADIUS_M} to {MAX_RADIUS_M.toLocaleString('en-GB')} m
        </p>
      )}
    </div>
  );
}

export interface GeofenceFormProps {
  geofence?: GeofenceRow | null;
  onSubmit: (payload: GeofencePayload) => void | Promise<void>;
  onCancel: () => void;
  versioning?: boolean;
  versionHistory?: boolean;
  /** ST-00282: the server has the zone Alerts switch (migration 072). */
  alertSwitch?: boolean;
  /** The tenant's zones, drawn faintly for context (the edited one is left out). */
  zones?: GeofenceRow[];
}

export default function GeofenceForm({
  geofence = null,
  onSubmit,
  onCancel,
  versioning = false,
  versionHistory = false,
  alertSwitch = false,
  zones = [],
}: GeofenceFormProps) {
  const isEditing = !!geofence;
  const savedShape = useMemo(() => shapeFromGeofence(geofence), [geofence]);
  const initialFields = useMemo(() => fieldsFrom(geofence), [geofence]);
  const [fields, setFields] = useState<ZoneFields>(initialFields);
  const [editor, dispatch] = useReducer(zoneEditorReducer, savedShape, initialEditorState);
  // LexiStar versioned configuration (only rendered/sent when `versioning`).
  const [config, setConfig] = useState<GeofenceVersionedForm>(() =>
    versionedFormFromGeofence(geofence)
  );
  const initialConfig = useRef(config);
  const [showHistory, setShowHistory] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [triedSave, setTriedSave] = useState(false);
  const [importNotes, setImportNotes] = useState<string[]>([]);
  const [overlays, setOverlays] = useState<EditorOverlay[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [snapping, setSnapping] = useState(true);
  const mapApi = useRef<ZoneEditorMapHandle | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Load aerial overlays
  useEffect(() => {
    api.aerialOverlays
      .list()
      .then((res: { data?: { data?: EditorOverlay[] } }) => setOverlays(res.data?.data || []))
      .catch(() => {});
  }, []);

  // Load location master data for the Site selector (and the search box)
  useEffect(() => {
    api.locations
      .list({ active: 'true' })
      .then((res: { data?: { locations?: LocationRow[] } }) =>
        setLocations(res.data?.locations || [])
      )
      .catch(() => {});
  }, []);

  // The workspace covers the page: stop the page behind it from scrolling, and
  // move focus into it (keyboard and screen-reader users start in the editor).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const otherZones = useMemo((): ContextZone[] => {
    const out: ContextZone[] = [];
    for (const z of zones) {
      if (z.is_active === false) continue;
      if (geofence && z.GeofenceID === geofence.GeofenceID) continue;
      const shape = shapeFromGeofence(z);
      if (shape) out.push({ id: z.GeofenceID, name: z.Name, shape, color: colorFor(z) });
    }
    return out;
  }, [zones, geofence]);

  const sites = useMemo(
    (): EditorSite[] =>
      locations
        .map((l) => ({
          id: l.location_id,
          name: l.location_name,
          at: [Number(l.longitude), Number(l.latitude)] as LngLat,
        }))
        // Only sites with a position on the map (latitude/longitude are optional, ORD-22).
        .filter(
          (s, i) =>
            locations[i]?.latitude != null &&
            isFiniteLngLat(s.at) &&
            !(s.at[0] === 0 && s.at[1] === 0)
        ),
    [locations]
  );

  // First camera: the saved zone, else all the tenant's zones, else South Africa.
  const initialBounds = useMemo((): Bounds | null => {
    if (savedShape) return shapeBounds(savedShape);
    let b: Bounds | null = null;
    for (const z of otherZones) {
      const zb = shapeBounds(z.shape);
      if (!zb) continue;
      b = b
        ? [
            [Math.min(b[0][0], zb[0][0]), Math.min(b[0][1], zb[0][1])],
            [Math.max(b[1][0], zb[1][0]), Math.max(b[1][1], zb[1][1])],
          ]
        : zb;
    }
    return b;
    // The first value only: the map frames it once on load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shape = editor.shape;
  const liveShape = editor.preview ?? editor.shape;
  const shapeChanged = !shapesEqual(shape, savedShape);
  // A saved legacy shape that is already invalid may be saved unchanged (rename, rules).
  const problems = useMemo(() => (liveShape ? shapeProblems(liveShape) : []), [liveShape]);
  const committedProblems = useMemo(() => (shape ? shapeProblems(shape) : []), [shape]);
  const blocking = useMemo(
    () => (shapeChanged ? committedProblems : []),
    [shapeChanged, committedProblems]
  );
  const crossings = useMemo(
    () => problems.find((p) => p.code === 'crosses-itself')?.points ?? [],
    [problems]
  );
  const warnings = useMemo(() => (shape ? shapeWarnings(shape) : []), [shape]);
  const overlaps = useMemo(
    () =>
      shape && !committedProblems.length
        ? otherZones.filter((z) => shapesOverlap(shape, z.shape))
        : [],
    [shape, committedProblems, otherZones]
  );

  const handleChange = <K extends keyof ZoneFields>(field: K, value: ZoneFields[K]): void => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const geometry = useMemo(
    () => (shape ? geometryFields(shape, geofence, savedShape) : null),
    [shape, geofence, savedShape]
  );

  const testDraft = useMemo(
    () => (geometry && !committedProblems.length ? draftGeometry({ ...geometry }) : null),
    [geometry, committedProblems]
  );

  const dirty =
    shapeChanged ||
    JSON.stringify(fields) !== JSON.stringify(initialFields) ||
    (versioning && config !== initialConfig.current);

  const handleCancel = useCallback(() => {
    if (dirty && !window.confirm('Discard your changes to this zone?')) return;
    onCancel();
  }, [dirty, onCancel]);

  // What still stands in the way of a save. Shown after the first Save press and
  // kept live, so each line disappears as soon as it is fixed.
  const saveErrors = useMemo(() => {
    const errs: string[] = [];
    if (!fields.name.trim()) errs.push('Give the zone a name.');
    if (!shape) errs.push('Draw the zone on the map first (Polygon, Rectangle or Circle).');
    else if (blocking.length) errs.push(...blocking.map((p) => p.message));
    if (versioning) errs.push(...validateVersionedForm(config));
    if (editor.draft) errs.push('Finish or cancel the shape you are drawing first.');
    return errs;
  }, [fields.name, shape, blocking, versioning, config, editor.draft]);
  const errors = triedSave ? saveErrors : [];

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setTriedSave(true);
    if (saveErrors.length || !shape || !geometry) {
      window.setTimeout(() => errorRef.current?.focus(), 0);
      return;
    }
    const payload = buildSavePayload({ ...fields, ...geometry }, config, { versioning, alertSwitch });
    // A saved polygon now drawn as a circle: clear its old corners.
    if (geometry.coordinates === null) payload.coordinates = null;
    try {
      setSubmitting(true);
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const zoomToShape = (s: ZoneShape | null): void => {
    const b = s ? shapeBounds(s) : null;
    if (b) mapApi.current?.fitBounds(b);
  };

  const handleExport = (): void => {
    if (!shape) return;
    const feature = shapeToFeature(shape, {
      name: fields.name || undefined,
      description: fields.description || undefined,
    });
    const blob = new Blob([JSON.stringify(feature, null, 2)], { type: 'application/geo+json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFileName(fields.name);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleSite = (value: string): void => {
    handleChange('linkedLocationId', value);
    const site = sites.find((s) => String(s.id) === String(value));
    if (site && !shape) mapApi.current?.flyTo(site.at, 16);
  };

  const area = shape ? shapeAreaM2(shape) : 0;
  const perimeter = shape ? shapePerimeterM(shape) : 0;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="zone-editor-title"
      tabIndex={-1}
      className="fixed inset-0 z-[8500] flex flex-col bg-surface focus:outline-none"
    >
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 bg-surface px-2 sm:gap-3 sm:px-4">
        <button
          type="button"
          onClick={handleCancel}
          aria-label="Close the zone editor"
          title="Close"
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={20} aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h2 id="zone-editor-title" className="truncate text-base font-semibold text-gray-900">
            {isEditing ? 'Edit Zone' : 'Create Zone'}
          </h2>
          <p className="truncate text-xs text-gray-500">
            {fields.name || 'New zone'}
            {isEditing && versioning && geofence?.CurrentVersion
              ? ` · saving makes version ${Number(geofence.CurrentVersion) + 1}`
              : ''}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="hidden rounded-lg border border-gray-300 bg-surface px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:inline-flex"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="zone-form"
            disabled={submitting}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {submitting ? 'Saving...' : isEditing ? 'Update Zone' : 'Create Zone'}
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Map (the main thing). isolate keeps maplibre's stacking context under the drawers. */}
        <section
          aria-label="Zone map"
          className="relative isolate z-0 h-[55dvh] min-h-[300px] shrink-0 lg:h-auto lg:min-h-0 lg:flex-1"
        >
          <ZoneEditor
            ref={mapApi}
            state={editor}
            dispatch={dispatch}
            otherZones={otherZones}
            overlays={overlays}
            overlayBase={
              import.meta.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5002'
            }
            sites={sites}
            problem={problems[0]?.message ?? null}
            crossings={crossings}
            initialBounds={initialBounds}
            shortcutsEnabled={!showImport && !showHistory}
            snapping={snapping}
            onSnappingChange={setSnapping}
          />
        </section>

        {/* Fields */}
        <form
          id="zone-form"
          onSubmit={handleSubmit}
          noValidate
          className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200 bg-surface lg:w-[400px] lg:flex-none lg:border-l lg:border-t-0 xl:w-[440px]"
        >
          <div className="space-y-5 p-4 sm:p-5">
            {errors.length > 0 && (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                data-testid="zone-form-errors"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 focus:outline-none"
              >
                <p className="font-medium">Before you save:</p>
                <ul className="mt-1 list-disc pl-5">
                  {errors.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Name and Description */}
            <div className="space-y-3">
              <div>
                <label htmlFor="zone-name" className={labelClass}>
                  Name *
                </label>
                <input
                  id="zone-name"
                  type="text"
                  value={fields.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. ROM Stockpile A"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="zone-description" className={labelClass}>
                  Description
                </label>
                <input
                  id="zone-description"
                  type="text"
                  value={fields.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className={inputClass}
                  placeholder="Optional description"
                />
              </div>
            </div>

            {/* Boundary summary */}
            <section
              aria-labelledby="zone-boundary-title"
              className="rounded-lg border border-gray-200 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 id="zone-boundary-title" className="text-sm font-semibold text-gray-900">
                  Zone Boundary *
                </h3>
                {shape && (
                  <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                    {shape.kind === 'circle' ? 'Circle' : 'Polygon'}
                  </span>
                )}
              </div>

              {shape ? (
                <dl className="mt-2 grid grid-cols-3 gap-2 text-sm" data-testid="zone-measurements">
                  <div>
                    <dt className="text-xs text-gray-500">Area</dt>
                    <dd className="font-semibold tabular-nums text-gray-900">{formatArea(area)}</dd>
                    {area >= 10000 && (
                      <dd className="text-xs tabular-nums text-gray-500">
                        {Math.round(area).toLocaleString('en-GB')} m²
                      </dd>
                    )}
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Perimeter</dt>
                    <dd className="font-semibold tabular-nums text-gray-900">
                      {formatLength(perimeter)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">
                      {shape.kind === 'circle' ? 'Radius' : 'Corners'}
                    </dt>
                    <dd className="font-semibold tabular-nums text-gray-900">
                      {shape.kind === 'circle' ? formatLength(shape.radiusM) : shape.ring.length}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Nothing drawn yet. Pick <strong>Polygon</strong>, <strong>Rectangle</strong> or{' '}
                  <strong>Circle</strong> on the map, or import a shape.
                </p>
              )}

              {shape?.kind === 'circle' && (
                <div className="mt-3">
                  <RadiusInput
                    radiusM={shape.radiusM}
                    onChange={(r) =>
                      dispatch({
                        type: 'commit',
                        shape: { ...shape, radiusM: r },
                        label: 'Change radius',
                        mergeKey: 'radius',
                        at: Date.now(),
                      })
                    }
                  />
                </div>
              )}

              {problems.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="zone-problems">
                  {problems.map((p) => (
                    <li
                      key={p.code}
                      className="rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700"
                    >
                      {p.message}
                      {!shapeChanged &&
                        ' This is how the zone was saved; you can still save other changes.'}
                    </li>
                  ))}
                </ul>
              )}
              {(warnings.length > 0 || overlaps.length > 0) && (
                <ul className="mt-3 space-y-1" data-testid="zone-warnings">
                  {warnings.map((w) => (
                    <li
                      key={w.code}
                      className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800"
                    >
                      {w.message}
                    </li>
                  ))}
                  {overlaps.length > 0 && (
                    <li className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                      Overlaps {overlaps.length === 1 ? 'zone' : `${overlaps.length} zones`}:{' '}
                      {overlaps
                        .slice(0, 4)
                        .map((z) => z.name)
                        .join(', ')}
                      {overlaps.length > 4 ? ` and ${overlaps.length - 4} more` : ''}. Trucks inside
                      both count for both. That is fine for a stockpile inside a site boundary.
                    </li>
                  )}
                </ul>
              )}
              {importNotes.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {importNotes.map((n) => (
                    <li key={n} className="text-xs text-gray-500">
                      {n}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className={smallButton} onClick={() => setShowImport(true)}>
                  <FileUp size={14} aria-hidden="true" />
                  Import
                </button>
                <button
                  type="button"
                  className={smallButton}
                  onClick={handleExport}
                  disabled={!shape}
                >
                  <Download size={14} aria-hidden="true" />
                  Export GeoJSON
                </button>
                <button
                  type="button"
                  className={smallButton}
                  onClick={() => zoomToShape(liveShape)}
                  disabled={!liveShape}
                >
                  <Focus size={14} aria-hidden="true" />
                  Zoom to zone
                </button>
                {isEditing && savedShape && shapeChanged && (
                  <button
                    type="button"
                    className={smallButton}
                    onClick={() => {
                      dispatch({ type: 'reset', shape: savedShape });
                      zoomToShape(savedShape);
                    }}
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                    Reset to saved
                  </button>
                )}
                {shape && (
                  <button
                    type="button"
                    className={smallButton}
                    onClick={() => dispatch({ type: 'clear' })}
                    title="Remove the shape and draw it again"
                  >
                    <Eraser size={14} aria-hidden="true" />
                    Clear
                  </button>
                )}
              </div>
            </section>

            {versioning ? (
              <>
                {/* Site (the purpose and the alert rules live in the sections below) */}
                <div>
                  <label className={labelClass}>Site</label>
                  <ThemedSelect
                    value={fields.linkedLocationId}
                    onChange={(e: { target: { value: unknown } }) =>
                      handleSite(String(e.target.value ?? ''))
                    }
                    isClearable
                    placeholder="No linked site"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">No linked site</option>
                    {locations.map((l) => (
                      <option key={l.location_id} value={l.location_id}>
                        {l.location_name}
                      </option>
                    ))}
                  </ThemedSelect>
                  <p className="text-xs text-gray-400 mt-1">
                    Which site this zone belongs to (optional)
                  </p>
                </div>

                <GeofenceConfigFields
                  value={config}
                  onChange={setConfig}
                  alertSwitch={alertSwitch}
                />

                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Test against history{' '}
                    <span className="text-gray-400 font-normal">
                      — how many entries and exits this zone, as drawn, would have produced
                    </span>
                  </span>
                  <TestAgainstHistory geofenceId={geofence?.GeofenceID ?? null} draft={testDraft} />
                </div>
              </>
            ) : (
              <>
                {/* Zone type and site. No alert settings: without the LexiStar configuration
                    (migration 021) there is no engine to raise them (ST-00269). */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <div>
                    <label className={labelClass}>Zone Type</label>
                    <ThemedSelect
                      value={fields.zoneType}
                      onChange={(e: { target: { value: unknown } }) =>
                        handleChange('zoneType', String(e.target.value ?? ''))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {ZONE_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.singular}
                        </option>
                      ))}
                    </ThemedSelect>
                  </div>
                  <div>
                    <label className={labelClass}>Site</label>
                    <ThemedSelect
                      value={fields.linkedLocationId}
                      onChange={(e: { target: { value: unknown } }) =>
                        handleSite(String(e.target.value ?? ''))
                      }
                      isClearable
                      placeholder="No linked site"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">No linked site</option>
                      {locations.map((l) => (
                        <option key={l.location_id} value={l.location_id}>
                          {l.location_name}
                        </option>
                      ))}
                    </ThemedSelect>
                    <p className="text-xs text-gray-400 mt-1">
                      Which site this zone belongs to (optional)
                    </p>
                  </div>
                </div>
              </>
            )}

            {versioning && versionHistory && isEditing && (
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowHistory(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FaHistory size={12} />
                  Version history
                  {geofence?.CurrentVersion ? ` (v${geofence.CurrentVersion})` : ''}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      <ZoneImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImport={(imported, name, notes) => {
          dispatch({ type: 'commit', shape: imported, label: 'Import shape' });
          dispatch({ type: 'tool', tool: 'edit' });
          setImportNotes(notes);
          if (!fields.name.trim() && name && !/^Shape \d+$/.test(name)) handleChange('name', name);
          setShowImport(false);
          window.setTimeout(() => zoomToShape(imported), 0);
        }}
      />

      {versioning && versionHistory && isEditing && (
        <GeofenceVersionHistory
          geofence={geofence}
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
